import { faShieldExclamation } from '@fortawesome/pro-regular-svg-icons'
import { Box, Alert, Button } from '@mantine/core'
import { useRouter } from 'next/router'
import { useState, useMemo } from 'react'

import { useAppDispatch } from '@flecto-io/core'
import { getBookingById } from '@flecto-io/core/src/actions'
import {
  useQueryGetAllBookingInsurancesQuote,
  useQueryGetAllBookingProducts,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct } from '@flecto/types'
import { Icon, iconInsuranceOn } from '@flecto/ui'
import { Page } from '@flecto/ui/saas'

import {
  useBookingInsuranceInfo,
  CardBookingInsuranceEstimatedCost,
  CardBookingInsuranceEnableIntegration,
} from '../../booking-insurances'
import {
  CardBookingProductsPickedUpSummary,
  CardBookingProductsReservedSummary,
  CardBookingProductsReturnedSummary,
} from '../../booking-products'
import { TableBookingProductsPickup } from '../components/TableBookingProductsPickup/TableBookingProductsPickup'
import { useModalBookingPickupConfirmation } from '../hooks/useModalBookingPickupConfirmation'
import styles from './styles.module.css'

export function PageBookingProductsPickup() {
  const router = useRouter()
  const bookingId = String(router.query.id)
  const [bookingProductsToPickup, setBookingProductsToPickup] = useState<
    CoreBookingProduct[]
  >([])
  const { t } = useTranslation('saas', { keyPrefix: 'booking_pickup_return' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })

  const dispatch = useAppDispatch()

  const { data: booking } = useQueryGetBookingById({ bookingId })

  const { data: bookingProducts, isLoading } = useQueryGetAllBookingProducts({
    bookingId,
  })

  const newTotalPickedupProducts = useMemo(
    () =>
      bookingProductsToPickup.reduce(
        (acc, bi) => acc + Number(bi.picked_up_quantity),
        booking?.meta?.total_picked_up_quantity ?? 0
      ),
    [bookingProductsToPickup, booking]
  )

  const {
    isBookingInsuranceEnabled,
    isTodayBetweenBookingDates,
    isAccountInsuranceEnabled,
    isBookingCoveredByInsurance,
    isEndAfterToday,
  } = useBookingInsuranceInfo({ bookingId })

  const { data: quotes, isLoading: isLoadingQuotes } =
    useQueryGetAllBookingInsurancesQuote({
      bookingId,
      bookingItems: bookingProductsToPickup,
      quantityField: 'picked_up_quantity',
      at_pickup: true,
    })

  const { openModal } = useModalBookingPickupConfirmation({
    bookingId,
    bookingProductsToPickup,
    quotes,
    isLoadingQuotes,
    onSuccess: () => {
      dispatch(getBookingById({ id: String(bookingId) }))
      navigationBack()
    },
  })

  const navigationBack = () => {
    router.push(`/bookings/${bookingId}`)
  }

  return (
    <Page withContainer>
      <Page.Close onClick={navigationBack} />
      <Page.Title>{t('page_pickup_title')}</Page.Title>
      <Page.Actions>
        <Button onClick={openModal}>{tActions('apply')}</Button>
      </Page.Actions>
      <Page.Content>
        {isBookingInsuranceEnabled && isTodayBetweenBookingDates && (
          <Alert
            mb='lg'
            color='blue'
            icon={<Icon icon={iconInsuranceOn} size='md' />}
          >
            {t('alert_pickup_insurance_description')}
          </Alert>
        )}
        {isBookingInsuranceEnabled && !isBookingCoveredByInsurance && (
          <Alert
            mb='lg'
            color='yellow'
            icon={<Icon icon={faShieldExclamation} size='md' />}
          >
            {t('alert_pickup_insurance_not_covered')}
          </Alert>
        )}
        {!isAccountInsuranceEnabled && (
          <CardBookingInsuranceEnableIntegration mb='lg' />
        )}
        <Box className={styles.summaryRowPickup} mb='lg'>
          <CardBookingProductsReservedSummary bookingId={bookingId} />

          <CardBookingProductsPickedUpSummary
            bookingId={bookingId}
            previewNewPickupTotal={newTotalPickedupProducts}
          />

          <CardBookingProductsReturnedSummary bookingId={bookingId} />
        </Box>

        <TableBookingProductsPickup
          mb='lg'
          isLoading={isLoading}
          bookingProducts={bookingProducts}
          onSelectBookingProducts={setBookingProductsToPickup}
        />

        {isEndAfterToday && (
          <CardBookingInsuranceEstimatedCost
            mt='lg'
            bookingId={bookingId}
            quotes={quotes}
            isLoadingQuotes={isLoadingQuotes}
          />
        )}
      </Page.Content>
    </Page>
  )
}
